import { Link } from "react-router-dom";
import { Logo, SIROD_LOGO_URL } from "@/components/Logo";
import {
  ShieldCheck, UserCheck, Building2, Home, Calendar, Crosshair,
  Truck, Camera, Music, BriefcaseBusiness, ArrowRight, MapPin,
} from "lucide-react";

const SERVICES = [
  { icon: UserCheck, title: "Personal & Executive Protection",
    body: "Professional bodyguard and VIP protection for executives, celebrities, government officials, diplomats, and high-net-worth individuals." },
  { icon: Building2, title: "Commercial Security",
    body: "Guard deployment for corporate offices, warehouses, hotels, retail stores, construction sites and residential estates." },
  { icon: Home, title: "Residential Security",
    body: "Comprehensive protection designed to safeguard families, homes and private properties." },
  { icon: Calendar, title: "Event Security",
    body: "Crowd control, VIP escort, access management and security coordination for concerts, conferences and private events." },
  { icon: Crosshair, title: "Armed & Unarmed Personnel",
    body: "Trained armed and unarmed guards deployed in line with operational requirements and Nigerian regulatory compliance." },
  { icon: Truck, title: "Mobile Patrol",
    body: "Routine patrol operations and rapid-response support for commercial and residential properties." },
  { icon: Camera, title: "CCTV Monitoring & Surveillance",
    body: "Technology-driven surveillance, access control support and incident reporting from a central operations centre." },
  { icon: ShieldCheck, title: "Logistics Escort",
    body: "Secure escort for valuable goods, executive transportation and sensitive logistics operations." },
  { icon: Music, title: "Nightlife & Venue Security",
    body: "Professional bouncers and venue security for clubs, lounges, concerts and entertainment facilities." },
  { icon: BriefcaseBusiness, title: "Security Consulting",
    body: "Risk assessment, security planning, vulnerability analysis and operational security advisory." },
];

const WHY_BULLETS = [
  "Highly trained security personnel",
  "Fast operational response",
  "Military & law enforcement experience",
  "Professional appearance and conduct",
  "Discreet VIP protection",
  "Affordable and scalable solutions",
  "Modern surveillance & tactical support",
];

export const Landing = () => (
  <div className="min-h-screen bg-navy">
    <LandingHeader />
    <Hero />
    <ServicesSection />
    <WhyChooseUs />
    <LandingFooter />
  </div>
);

const LandingHeader = () => (
  <header style={{ borderBottom: "1px solid var(--border-dark)" }}>
    <div className="max-w-6xl mx-auto px-6 py-6 flex items-center justify-between">
      <Logo size={64} />
      <div className="flex items-center gap-5">
        <Link to="/about" className="text-muted hover:text-white" style={{ fontSize: 14 }} data-testid="nav-about">About us</Link>
        <a href="#services" className="text-muted hover:text-white" style={{ fontSize: 14 }}>Services</a>
        <Link to="/contact" className="text-muted hover:text-white" style={{ fontSize: 14 }} data-testid="nav-contact">Contact</Link>
        <Link to="/login" className="btn-outline" data-testid="nav-login-btn">Sign in</Link>
      </div>
    </div>
  </header>
);

const Hero = () => (
  <section className="max-w-6xl mx-auto px-6 pt-20 pb-16">
    <div className="grid md:grid-cols-2 gap-12 items-center">
      <div>
        <div className="badge badge-neutral mb-6 inline-flex items-center gap-2">
          <MapPin size={12} /> Abuja · Lagos · Port Harcourt
        </div>
        <h1 style={{ fontSize: 56, lineHeight: 1.05, fontWeight: 500, letterSpacing: "-0.02em" }}>
          Protection you can trust.
        </h1>
        <p className="text-muted mt-6" style={{ fontSize: 17, lineHeight: 1.6, maxWidth: 540 }}>
          Sirod Secur is a modern private security company based in Abuja, delivering
          professional personal and commercial protection to individuals, businesses,
          and high-profile clients across Nigeria — backed by trained tactical
          personnel and a technology-driven operations centre.
        </p>
        <div className="flex gap-3 mt-8">
          <Link to="/login" className="btn-primary inline-flex items-center gap-2" data-testid="hero-cta">
            Open the platform <ArrowRight size={16} />
          </Link>
          <Link to="/about" className="btn-outline">About us</Link>
        </div>
        <div className="mt-12 flex items-center gap-8 text-muted" style={{ fontSize: 13 }}>
          <Stat value="10+" label="service lines" />
          <Stat value="24/7" label="operations centre" />
          <Stat value="99.5%" label="tour completion" />
        </div>
      </div>
      <div className="s-card" style={{ padding: 24 }}>
        <img src={SIROD_LOGO_URL} alt="Sirod Secur" style={{ width: "100%", borderRadius: 12 }} />
      </div>
    </div>
  </section>
);

const Stat = ({ value, label }) => (
  <div><span style={{ color: "#fff", fontSize: 22 }}>{value}</span> {label}</div>
);

const ServicesSection = () => (
  <section id="services" className="max-w-6xl mx-auto px-6 pb-24">
    <div className="mb-8">
      <div className="text-teal" style={{ fontSize: 12, letterSpacing: "0.1em", textTransform: "uppercase", marginBottom: 6 }}>What we do</div>
      <h2 className="h-section" style={{ fontSize: 28 }}>Security services, end to end</h2>
      <p className="text-muted mt-3" style={{ fontSize: 15, maxWidth: 640 }}>
        From a single VIP detail to a full residential estate, our trained
        personnel and tactical command centre deliver layered protection
        tailored to each client.
      </p>
    </div>
    <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
      {SERVICES.map(s => <ServiceCard key={s.title} {...s} />)}
    </div>
  </section>
);

const ServiceCard = ({ icon: Icon, title, body }) => (
  <div className="s-card" data-testid={`service-${title}`}>
    <div style={{ width: 36, height: 36, borderRadius: 8, background: "rgba(46,202,200,0.12)",
      display: "flex", alignItems: "center", justifyContent: "center", marginBottom: 12 }}>
      <Icon size={18} color="#2ECAC8" />
    </div>
    <div style={{ fontSize: 16, fontWeight: 500, marginBottom: 4 }}>{title}</div>
    <div className="text-muted" style={{ fontSize: 13, lineHeight: 1.5 }}>{body}</div>
  </div>
);

const WhyChooseUs = () => (
  <section className="max-w-6xl mx-auto px-6 pb-24">
    <div className="s-card" style={{ padding: 40 }}>
      <div className="grid md:grid-cols-2 gap-10 items-center">
        <div>
          <div className="text-teal" style={{ fontSize: 12, letterSpacing: "0.1em", textTransform: "uppercase", marginBottom: 6 }}>Why choose us</div>
          <h3 className="h-section" style={{ fontSize: 24 }}>Tactical experience, modern systems, discreet conduct.</h3>
          <p className="text-muted mt-3" style={{ fontSize: 14, lineHeight: 1.7 }}>
            Our team is built from professionals with military and law-enforcement
            backgrounds, trained to handle high-risk situations with the discretion
            and professionalism every client expects.
          </p>
        </div>
        <ul className="grid grid-cols-1 gap-3">
          {WHY_BULLETS.map(item => (
            <li key={item} className="flex items-center gap-3" style={{ fontSize: 14 }}>
              <span style={{ width: 6, height: 6, borderRadius: "50%", background: "#2ECAC8" }} />
              {item}
            </li>
          ))}
        </ul>
      </div>
    </div>
  </section>
);

const LandingFooter = () => (
  <footer style={{ borderTop: "1px solid var(--border-dark)" }}>
    <div className="max-w-6xl mx-auto px-6 py-6 flex items-center justify-between text-muted" style={{ fontSize: 13 }}>
      <div>© 2026 Sirod Secur · Protection you can trust</div>
      <div>Headquarters · Abuja, Nigeria · admin@sirodsecur.com</div>
    </div>
  </footer>
);
